import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEO } from '../components/SEO';
import { ArrowRight } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: 'easeOut' },
  }),
};

const sections = [
  {
    tag: 'The Hunt',
    title: 'We source from the places others overlook.',
    text: 'Flea markets in Paris, warehouse clearances in Manchester, private sellers in Milan. Every piece we list was found because we went looking for it. No algorithms. No bulk buying. Just instinct built over 12 years of doing this.',
    image: 'https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/bupqsq2p_ChatGPT%20Image%20Mar%2021%2C%202026%2C%2005_25_50%20PM.png',
    imageAlt: 'Vintage sourcing at flea market',
    reverse: false,
  },
  {
    tag: 'The Grade',
    title: 'Every piece is inspected, graded, and photographed honestly.',
    text: 'We check stitching, zips, fabric integrity, and overall feel. Our grading system — Excellent, Good, Vintage Worn — tells you exactly what to expect. No surprises. No hidden flaws. What you see is genuinely what you get.',
    image: 'https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/olnn3df5_ChatGPT%20Image%20Mar%2021%2C%202026%2C%2005_34_06%20PM.png',
    imageAlt: 'Quality inspection of vintage jacket',
    reverse: true,
  },
  {
    tag: 'The Promise',
    title: 'One-of-a-kind. Gone forever once sold.',
    text: 'We never restock. Every item on our site is a singular find. When it sells, it\'s gone. That\'s what makes vintage special — you\'re wearing something nobody else can buy. We take that seriously.',
    image: 'https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/evqwk720_ChatGPT%20Image%20Mar%2021%2C%202026%2C%2005_31_48%20PM.png',
    imageAlt: 'Unique vintage streetwear collection',
    reverse: false,
  },
];

export const About = () => {
  return (
    <div className="w-full" data-testid="about-page">
      <SEO title="About Us" description="We find the pieces that deserve a second life. 12 years of sourcing authentic one-of-a-kind vintage." path="/about" />

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/zhcmhslm_ChatGPT%20Image%20Mar%2021%2C%202026%2C%2006_09_25%20PM.png"
            alt="Vintage Wardrobe Co"
            className="w-full h-full object-cover object-top"
            loading="eager"
            width={1440}
            height={900}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
        </div>
        <div className="relative z-10 text-center max-w-3xl px-6">
          <motion.p
            className="text-[11px] uppercase tracking-[0.3em] text-white/60 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Est. 2012
          </motion.p>
          <motion.h1
            className="text-[32px] md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-5 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            data-testid="about-title"
          >
            We find the pieces that<br />deserve a second life.
          </motion.h1>
          <motion.p
            className="text-white/60 text-base md:text-lg max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.6 }}
          >
            12 years of sourcing. Thousands of finds. Every one chosen by hand.
          </motion.p>
        </div>
      </section>

      {/* Three Sections — Alternating Layout */}
      {sections.map((section, i) => (
        <section key={i} className={`py-16 md:py-24 ${i % 2 === 0 ? 'bg-[#F9F8F6]' : 'bg-[#F0EEEB]'}`} data-testid={`about-section-${i}`}>
          <div className="container-custom max-w-6xl">
            <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center ${section.reverse ? 'md:direction-rtl' : ''}`}>
              {/* Image */}
              <motion.div
                className={`aspect-square overflow-hidden ${section.reverse ? 'md:order-2' : 'md:order-1'}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={section.image}
                  alt={section.imageAlt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={600}
                  height={600}
                />
              </motion.div>

              {/* Text */}
              <motion.div
                className={section.reverse ? 'md:order-1' : 'md:order-2'}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.span variants={fadeUp} custom={0} className="text-[9px] uppercase tracking-[0.35em] text-[#C9A84C] font-semibold block mb-2">
                  {section.tag}
                </motion.span>
                <motion.h2 variants={fadeUp} custom={1} className="text-2xl md:text-[32px] font-heading font-bold tracking-tight mb-5 leading-tight">
                  {section.title}
                </motion.h2>
                <motion.p variants={fadeUp} custom={2} className="text-[#666] text-base leading-relaxed">
                  {section.text}
                </motion.p>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Final CTA */}
      <section className="relative py-24 md:py-32 flex items-center justify-center overflow-hidden bg-[#0A0A0A]" data-testid="about-cta">
        <div className="relative z-10 text-center px-6 max-w-2xl">
          <p className="text-white/40 text-sm uppercase tracking-[0.3em] mb-4">Every piece tells a story</p>
          <h2 className="text-[32px] md:text-5xl font-heading font-bold text-white tracking-tight mb-8 leading-tight">
            Now it's yours.
          </h2>
          <Link to="/shop">
            <button className="bg-white text-[#0A0A0A] px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#C9A84C] hover:text-white transition-all duration-300 inline-flex items-center gap-2" data-testid="about-final-cta">
              Shop the Collection <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};
